import _ from "lodash";
import moment from "moment";
import React, { useState, useEffect, useLayoutEffect } from "react";

import { adaptRadioFields } from "@/features/notes/(v2)/(advanced-care)/helpers/adapters";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { durationToString } from "@/utils/momentUtils";
import images from "@/utils/images";
import { isAuthorized } from "@/utils/authToken";
import { providerRequests } from "@/api/(requests)/provider";
import { showToast } from "@/providers/ToastProvider";

import { EndCallDecisionModal } from "./EndCallDecisionModal";

import "./Steps.styles.scss";

export function RemoteVideo(props) {
  const {
    allottedTime,
    attachRemoteTracks,
    callDetails,
    endCall,
    note,
    participantDisconnected,
    redirectToMessageProviderDialog,
    remoteMedia,
    remoteTracks,
    timer,
    toggleAudioBtn,
    toggleVideoBtn,
    workflow,
  } = props;
  const isUserPatient = isAuthorized("user:patient");
  const [showEndCallModal, setShowEndCallModal] = useState(false);
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [_dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });

  const existingNoShowReason = note ? note.content.patient_no_show_reason : "";
  const [noShowReason, setNoShowReason] = useState(existingNoShowReason);

  useEffect(() => {
    function handleResize() {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      });
    }

    window.addEventListener("resize", handleResize);

    return (_) => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useLayoutEffect(() => {
    attachRemoteTracks();
  }, [attachRemoteTracks]);

  // Pre-existing: `note` is the same object reference as
  // ProviderVideoWorkflowStep's this.state.progressNote (passed down
  // unspread) — these mutate it directly rather than cloning first,
  // bypassing setState. Works today only because no sibling step
  // re-reads progressNote before this call resolves; clone before
  // mutating if that assumption ever changes.
  const updateNoteToBeNoShow = async () => {
    if (workflow.details.showNoShowModalOnly) {
      const noShowArray = _.get(note, "content.patient_no_show_array");

      const no_show = {
        call_id: callDetails.callId,
        patient_no_show: true,
        patient_no_show_reason: noShowReason,
        patient_no_show_time: moment.utc().format(),
        provider_id: callDetails.provider_username,
      };

      if (!noShowArray) {
        note.content.patient_no_show_array = [no_show];
      } else {
        if (
          note.content.patient_no_show_array.find(
            (element) => element.call_id === callDetails.callId,
          )
        ) {
          return;
        }

        note.content.patient_no_show_array.push(no_show);
      }
    } else {
      note.is_billable = false;
      note.content.patient_no_show = true;
      note.content.patient_no_show_reason = noShowReason;
    }

    try {
      const urlParams = { patientId: callDetails.patient_username };

      return providerRequests.put_progress_note({
        context: { showLoader: false },
        data: note,
        urlParams,
      });
    } catch (e) {
      captureErrorMessage(parseError({ error: e, fallback: "Failed to save no-show note" }), {
        tags: {
          component: "RemoteVideo",
          errorType: "submit_no_show_note",
          patientId: callDetails.patient_username,
        },
      });
    }
  };

  // ACP Meeting: flips patient_participant_attest false on no-show, then
  // hands off to endCall() -> ACAA form (step 2), instead of the generic
  // updateNoteToBeNoShow + hard-navigate path.
  const handleAcaaNoShow = async () => {
    note.content.patient_participant_attest = false;
    // not setting patient_no_show=true: backend treats false->true as a
    // cancellation/SMS trigger, unused by ACAA
    note.content.patient_no_show_reason = noShowReason;
    // casts remaining "" radio sentinels to boolean/null; backend 400s otherwise
    note.content = adaptRadioFields(note.content);

    try {
      const urlParams = { patientId: callDetails.patient_username };

      await providerRequests.put_progress_note({
        context: { showLoader: false },
        data: note,
        urlParams,
      });
    } catch (e) {
      captureErrorMessage(parseError({ error: e, fallback: "Failed to save ACAA no-show" }), {
        tags: {
          component: "RemoteVideo",
          errorType: "submit_acaa_no_show",
          patientId: callDetails.patient_username,
        },
      });
      showToast([{ heading: "Failed to save no-show note.", variant: "error" }]);
    } finally {
      endCall();
    }
  };

  const handleNoShow = async () => {
    if (workflow.details.createAcaaAssessment) {
      await handleAcaaNoShow();

      return;
    }

    updateNoteToBeNoShow().then(() => {
      props.history.push({
        pathname: `/app/patient/${callDetails.patient_username}/profile/v2?selectedTab=Notes`,
      });
    });
  };

  let timerStyle = {};
  let buttonGrpStyle = {};

  let endCallBtn = (
    <button className="GuidedVideoCall__controls-btn GuidedVideoCall__controls-btn--endcall">
      <img
        alt="endCall"
        className="mx-3 d-block cursor"
        onClick={() => {
          if (isUserPatient || workflow.details.skipEndCallModal) {
            if (remoteTracks.length === 0 && !participantDisconnected) {
              redirectToMessageProviderDialog();

              return;
            }

            endCall();

            return;
          }
          if (workflow.details.showNoShowModalOnly) {
            setShowNoShowModal(true);

            return;
          }
          setShowEndCallModal(true);
        }}
        src={images("./icons/end-call-white.svg")}
        style={{ "margin-top": "5px", width: "65px" }}
      />
    </button>
  );

  let additional;

  if (remoteTracks.length === 0 && !participantDisconnected) {
    if (isUserPatient) {
      additional = (
        <div className="GuidedVideoCall__awaiting_message order-1 order-lg-12 body-md tw:absolute! tw:inset-0">
          <p>
            <span className="fs-20">
              <strong>Awaiting your session to start</strong>
            </span>
            <span>Please hang tight while the other party joins.</span>
          </p>
        </div>
      );
    } else {
      additional =
        workflow["details"] && workflow["details"]["skipEndCallModal"] ? (
          <div className="GuidedVideoCall__awaiting_message order-1 order-lg-12 body-md tw:absolute! tw:inset-0">
            <p>
              <span className="fs-20 mb-2">
                <strong>Awaiting the patient to join.</strong>
              </span>

              <span className="fs-14 mb-2 tw:font-normal">
                Please hang tight while the other party joins. If the patient does not join, please
                end the call
              </span>
            </p>
          </div>
        ) : (
          <div className="GuidedVideoCall__awaiting_message order-1 order-lg-12 body-md tw:absolute! tw:inset-0">
            <p>
              <span className="fs-14 mb-2 tw:font-normal">
                Please hang tight while the other party joins. If the patient does not join
              </span>

              <span className="fs-14 tw:font-normal">
                end the call below and select the patient did not attend
              </span>
            </p>
          </div>
        );
    }
  }

  let allottedDuration = moment.duration(allottedTime, "minutes");

  return (
    <>
      <div className="GuidedVideoCall__timer" style={timerStyle}>
        {timer} / <span style={{ color: "#BACED7" }}>{durationToString(allottedDuration)}</span>
      </div>

      <div className="GuidedVideoCall__remote m-auto">
        {remoteTracks.length > 0 ? (
          remoteMedia
        ) : (
          <div
            className="GuidedVideoCall__remote_placeholder m-auto"
            ref={props.remoteVideoPlaceholderRef}
          />
        )}
      </div>

      <div
        className="GuidedVideoCall__controls d-flex d-lg-flex align-items-center tw:justify-center"
        style={buttonGrpStyle}
      >
        <div className="GuidedVideoCall__controls-inner d-flex">
          {toggleAudioBtn}
          {endCallBtn}
          {toggleVideoBtn}
        </div>
      </div>

      <EndCallDecisionModal
        isOpen={showEndCallModal}
        leftButtonLabel="Review Note"
        leftHelperText="This will exit the call and save a draft of your session"
        leftText="You will be able to review the summary of the note and save it as a draft or sign and submit."
        leftTitle="Review Draft Note"
        noShowReason={noShowReason}
        onClose={setShowEndCallModal}
        onLeftButtonClick={endCall}
        onNoShowClick={handleNoShow}
        onNoShowReasonChange={setNoShowReason}
      />

      <EndCallDecisionModal
        isOpen={showNoShowModal}
        leftButtonLabel="End Call"
        leftHelperText="This will exit the call"
        leftText="Once the call is complete, please complete the Initial Clinical Assessment for the member"
        leftTitle="End Call"
        noShowReason={noShowReason}
        onClose={setShowNoShowModal}
        onLeftButtonClick={endCall}
        onNoShowClick={handleNoShow}
        onNoShowReasonChange={setNoShowReason}
      />

      {additional}

      <div className="GuidedVideoCall__mobile_warning d-block d-md-none d-lg-none text-white">
        ⓘ Looking for the full experience? Log in via desktop.
      </div>
    </>
  );
}
